import classNames from "classnames";
import { createPortal } from "react-dom";
import { type CSSProperties, type FC, useCallback, useEffect, useRef, useState } from "react";
import { Button, Dropdown, Form } from "react-bootstrap";

import "../list-header/list-header.css";
import "./inquiry-column-filters.css";
import { useSuppressBrowserAutocomplete } from "../forms/suppress-browser-autocomplete";
import {
  MMYYYY_REGEX,
  mmyyyyToMonthInput,
  monthInputValueToMMYYYY,
  utcFirstOfMonthFromMMYYYY
} from "./inquiry-filter-date-utils";

/** Bare filter trigger: no outline "box" — only the bars (matches ACP column header). */
export const inquiryColumnFilterToggleClassName = "inquiry-column-filter-toggle inquiry-column-filter-toggle--bare";

/** Width/height aligned to `AcpColumnFilterIcon` for Student column layout (no button chrome). */
export const inquiryColumnFilterSizerClassName = "inquiry-column-filter-spacer inquiry-column-filter-toggle--bare-gap";

/** Inline backstop so styles beat `public/css/custom-shared.css` `.dropdown .dropdown-toggle` when link order wins. */
const inquiryColumnFilterToggleStyle: CSSProperties = {
  width: "auto",
  maxWidth: "none",
  height: "0.72em",
  minHeight: 0,
  maxHeight: "0.72em",
  padding: 0,
  margin: 0,
  border: "none",
  borderRadius: 0,
  boxShadow: "none",
  background: "none",
  backgroundColor: "transparent",
  backgroundImage: "none",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  lineHeight: 1,
  verticalAlign: "middle"
};

/** ACP-style filter: three solid dark gray bars, centered funnel — no surrounding control box. */
export const AcpColumnFilterIcon: FC<{ className?: string }> = ({ className }) => (
  <svg
    className={classNames("inquiry-acp-filter-svg", className)}
    width={11}
    height={7}
    viewBox="0 0 11 7"
    aria-hidden
    focusable="false"
  >
    <rect className="inquiry-acp-filter-line" x="0" y="0" width="11" height="1.5" rx="0.2" />
    <rect className="inquiry-acp-filter-line" x="1.75" y="2.75" width="7.5" height="1.5" rx="0.2" />
    <rect className="inquiry-acp-filter-line" x="3.75" y="5.5" width="3.5" height="1.5" rx="0.2" />
  </svg>
);

/**
 * Measures the nearest `<th>` ancestor of `toggleEl` and returns a `position: fixed` style
 * that places a panel flush below it, spanning its full width.
 * Re-calculates on scroll and resize while `open` is true.
 *
 * @param minWidthFromHeaderThIndex If set, panel width is at least that `<th>`'s width in the same header row (0-based).
 * @param panelWidthMultiplier Scales this column's width before applying the min-width rule (e.g. `0.75` for a narrower panel).
 * @param panelMinWidthMatchScale Scales the reference `<th>` width used for the min-width rule (default `1`; e.g. `0.9` for slightly narrower than that column).
 */
function useThPanelStyle(
  open: boolean,
  toggleRef: React.RefObject<HTMLElement | null>,
  minWidthFromHeaderThIndex?: number,
  panelWidthMultiplier?: number,
  panelMinWidthMatchScale?: number
): CSSProperties {
  const [style, setStyle] = useState<CSSProperties>({});

  const recalc = useCallback(() => {
    const el = toggleRef.current;
    if (!el) {
      return;
    }
    const th = el.closest("th");
    if (!th) {
      return;
    }
    const tr = th.closest("tr");
    const own = th.getBoundingClientRect();
    let width = own.width * (panelWidthMultiplier ?? 1);
    if (minWidthFromHeaderThIndex !== undefined && tr) {
      const other = tr.children.item(minWidthFromHeaderThIndex) as HTMLElement | null;
      if (other) {
        const otherW = other.getBoundingClientRect().width * (panelMinWidthMatchScale ?? 1);
        width = Math.max(width, otherW);
      }
    }
    setStyle({ position: "fixed", top: own.bottom, left: own.left, width, zIndex: 1050 });
  }, [toggleRef, minWidthFromHeaderThIndex, panelWidthMultiplier, panelMinWidthMatchScale]);

  useEffect(() => {
    if (!open) {
      return;
    }
    recalc();
    window.addEventListener("scroll", recalc, true);
    window.addEventListener("resize", recalc);
    return () => {
      window.removeEventListener("scroll", recalc, true);
      window.removeEventListener("resize", recalc);
    };
  }, [open, recalc]);

  return style;
}

export interface CheckboxFilterOption {
  label: string;
  value: string;
}

interface MultiCheckboxColumnFilterProps {
  options: CheckboxFilterOption[];
  selected: string[];
  onSelectedChange: (values: string[]) => void;
  "aria-label": string;
  /** When set, the checkbox list scrolls so the menu stays compact (e.g. many services vs few statuses). */
  optionsScrollMaxHeight?: string;
  /**
   * When set, the open panel is at least as wide as the header `<th>` at this index in the same row
   * (0-based; e.g. `0` = Student column to widen the Service filter panel).
   */
  panelMinWidthHeaderThIndex?: number;
  /** Multiplier for this column's width before the min-width rule (default `1`). Use under 1 to narrow (e.g. Status). */
  panelWidthMultiplier?: number;
  /**
   * Scales the reference header width when using `panelMinWidthHeaderThIndex` (default `1`).
   * Use slightly under `1` to be a bit narrower than that column while still fitting long footer text.
   */
  panelMinWidthMatchScale?: number;
}

export const MultiCheckboxColumnFilter: FC<MultiCheckboxColumnFilterProps> = ({
  options,
  selected,
  onSelectedChange,
  "aria-label": ariaLabel,
  optionsScrollMaxHeight,
  panelMinWidthHeaderThIndex,
  panelWidthMultiplier,
  panelMinWidthMatchScale
}) => {
  const [open, setOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelStyle = useThPanelStyle(
    open,
    toggleRef,
    panelMinWidthHeaderThIndex,
    panelWidthMultiplier,
    panelMinWidthMatchScale
  );

  const toggle = (value: string) => {
    if (selected.includes(value)) {
      onSelectedChange(selected.filter(v => v !== value));
    } else {
      onSelectedChange([...selected, value]);
    }
  };

  const clearAll = () => onSelectedChange([]);

  const filterActive = selected.length > 0;
  /** Checked when no individual filter is active — means "showing all". */
  const selectAllChecked = selected.length === 0;
  const selectAllRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = false;
    }
  }, [selectAllChecked]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const handler = (e: MouseEvent) => {
      if (panelRef.current?.contains(e.target as Node) || toggleRef.current?.contains(e.target as Node)) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener("mousedown", handler, true);
    return () => document.removeEventListener("mousedown", handler, true);
  }, [open]);

  return (
    <span
      style={{ display: "inline-flex", alignItems: "center", marginLeft: "0.25rem" }}
      onClick={e => e.stopPropagation()}
    >
      <button
        ref={toggleRef}
        type="button"
        className={classNames(
          "btn",
          inquiryColumnFilterToggleClassName,
          filterActive && "inquiry-column-filter-toggle--active"
        )}
        style={inquiryColumnFilterToggleStyle}
        aria-label={ariaLabel}
        aria-expanded={open}
        title="Open column menu: filter"
        onClick={e => {
          e.stopPropagation();
          setOpen(o => !o);
        }}
      >
        <AcpColumnFilterIcon />
      </button>
      {open &&
        createPortal(
          <div
            ref={panelRef}
            className="inquiry-column-filter-panel"
            style={panelStyle}
            onClick={e => e.stopPropagation()}
          >
            <div className="px-2 py-1 border-bottom">
              <Form.Check
                ref={selectAllRef}
                type="checkbox"
                id={`inquiry-filter-${ariaLabel}-select-all`}
                label="Select All"
                checked={selectAllChecked}
                onChange={clearAll}
                className="inquiry-column-filter-select-all"
              />
            </div>
            <div
              className={classNames(
                "inquiry-column-filter-options-list",
                optionsScrollMaxHeight && "inquiry-column-filter-options-scroll"
              )}
              style={optionsScrollMaxHeight ? { maxHeight: optionsScrollMaxHeight } : undefined}
            >
              {options.map(opt => (
                <div key={opt.value} className="inquiry-column-filter-option py-1 px-2">
                  <Form.Check
                    type="checkbox"
                    id={`inquiry-filter-${ariaLabel}-${opt.value}`}
                    label={opt.label}
                    checked={selected.includes(opt.value)}
                    onChange={() => toggle(opt.value)}
                    onClick={e => e.stopPropagation()}
                  />
                </div>
              ))}
            </div>
            <div className="px-2 py-1 border-top">
              <button type="button" className="inquiry-column-filter-clear-btn" onClick={clearAll}>
                Clear Selection
              </button>
            </div>
          </div>,
          document.body
        )}
    </span>
  );
};

interface StageStartDateRangeFilterProps {
  fromValue: string | undefined;
  toValue: string | undefined;
  onApply: (from: string | undefined, to: string | undefined) => void;
  "aria-label": string;
}

export const StageStartDateRangeFilter: FC<StageStartDateRangeFilterProps> = ({
  fromValue,
  toValue,
  onApply,
  "aria-label": ariaLabel
}) => {
  const [open, setOpen] = useState(false);
  const [draftFromMonth, setDraftFromMonth] = useState("");
  const [draftToMonth, setDraftToMonth] = useState("");
  const [error, setError] = useState<string | null>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelStyle = useThPanelStyle(open, toggleRef);
  const fromMonthAutocompleteSuppression = useSuppressBrowserAutocomplete(undefined, { type: "month" });
  const toMonthAutocompleteSuppression = useSuppressBrowserAutocomplete(undefined, { type: "month" });

  useEffect(() => {
    if (open) {
      setDraftFromMonth(mmyyyyToMonthInput(fromValue));
      setDraftToMonth(mmyyyyToMonthInput(toValue));
      setError(null);
    }
  }, [open, fromValue, toValue]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const handler = (e: MouseEvent) => {
      if (panelRef.current?.contains(e.target as Node) || toggleRef.current?.contains(e.target as Node)) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener("mousedown", handler, true);
    return () => document.removeEventListener("mousedown", handler, true);
  }, [open]);

  const validatePair = (fromMonth: string, toMonth: string): boolean => {
    setError(null);
    const fromMm = fromMonth ? monthInputValueToMMYYYY(fromMonth) : undefined;
    const toMm = toMonth ? monthInputValueToMMYYYY(toMonth) : undefined;
    if (!fromMm && !toMm) {
      return true;
    }
    if (fromMonth && !fromMm) {
      setError("From month is invalid");
      return false;
    }
    if (toMonth && !toMm) {
      setError("To month is invalid");
      return false;
    }
    if (fromMm && !MMYYYY_REGEX.test(fromMm)) {
      setError("From must be a valid month");
      return false;
    }
    if (toMm && !MMYYYY_REGEX.test(toMm)) {
      setError("To must be a valid month");
      return false;
    }
    if (fromMm && toMm) {
      const a = utcFirstOfMonthFromMMYYYY(fromMm);
      const b = utcFirstOfMonthFromMMYYYY(toMm);
      if (!Number.isFinite(a) || !Number.isFinite(b) || a > b) {
        setError("From must be on or before To");
        return false;
      }
    }
    return true;
  };

  const apply = () => {
    if (!validatePair(draftFromMonth, draftToMonth)) {
      return;
    }
    const f = draftFromMonth ? monthInputValueToMMYYYY(draftFromMonth) : undefined;
    const t = draftToMonth ? monthInputValueToMMYYYY(draftToMonth) : undefined;
    onApply(f, t);
    setOpen(false);
  };

  const clear = () => {
    setDraftFromMonth("");
    setDraftToMonth("");
    setError(null);
    onApply(undefined, undefined);
    setOpen(false);
  };

  const filterActive = Boolean(fromValue || toValue);

  return (
    <span
      style={{ display: "inline-flex", alignItems: "center", marginLeft: "0.25rem" }}
      onClick={e => e.stopPropagation()}
    >
      <button
        ref={toggleRef}
        type="button"
        className={classNames(
          "btn",
          inquiryColumnFilterToggleClassName,
          filterActive && "inquiry-column-filter-toggle--active"
        )}
        style={inquiryColumnFilterToggleStyle}
        aria-label={ariaLabel}
        aria-expanded={open}
        title="Open column menu: filter by month range"
        onClick={e => {
          e.stopPropagation();
          setOpen(o => !o);
        }}
      >
        <AcpColumnFilterIcon />
      </button>
      {open &&
        createPortal(
          <div
            ref={panelRef}
            className="inquiry-column-filter-panel inquiry-column-filter-panel--date-range"
            style={panelStyle}
            onClick={e => e.stopPropagation()}
          >
            <div className="px-2 py-2">
              <div className="small text-muted mb-2">Filter by month and year</div>
              <Form.Group className="mb-2" controlId="stage-start-from">
                <Form.Label className="small mb-0">From</Form.Label>
                <Form.Control
                  type="month"
                  size="sm"
                  value={draftFromMonth}
                  onChange={e => setDraftFromMonth(e.target.value)}
                  onClick={e => e.stopPropagation()}
                  readOnly={fromMonthAutocompleteSuppression.readOnly}
                  onFocus={fromMonthAutocompleteSuppression.onFocus}
                  autoComplete={fromMonthAutocompleteSuppression.autoComplete}
                  autoCorrect={fromMonthAutocompleteSuppression.autoCorrect}
                  spellCheck={fromMonthAutocompleteSuppression.spellCheck}
                  data-1p-ignore={fromMonthAutocompleteSuppression["data-1p-ignore"]}
                  data-lpignore={fromMonthAutocompleteSuppression["data-lpignore"]}
                />
              </Form.Group>
              <Form.Group className="mb-2" controlId="stage-start-to">
                <Form.Label className="small mb-0">To</Form.Label>
                <Form.Control
                  type="month"
                  size="sm"
                  value={draftToMonth}
                  onChange={e => setDraftToMonth(e.target.value)}
                  onClick={e => e.stopPropagation()}
                  readOnly={toMonthAutocompleteSuppression.readOnly}
                  onFocus={toMonthAutocompleteSuppression.onFocus}
                  autoComplete={toMonthAutocompleteSuppression.autoComplete}
                  autoCorrect={toMonthAutocompleteSuppression.autoCorrect}
                  spellCheck={toMonthAutocompleteSuppression.spellCheck}
                  data-1p-ignore={toMonthAutocompleteSuppression["data-1p-ignore"]}
                  data-lpignore={toMonthAutocompleteSuppression["data-lpignore"]}
                />
              </Form.Group>
              {error && <div className="text-danger small mb-2">{error}</div>}
              <div className="d-flex justify-content-between align-items-center gap-2 mt-1">
                <Button size="sm" variant="primary" type="button" onClick={apply}>
                  Apply
                </Button>
                <button type="button" className="inquiry-column-filter-clear-btn flex-shrink-0" onClick={clear}>
                  Clear
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </span>
  );
};

export interface InquiryListSortMenuProps {
  sortBy: string;
  onApplySort: (apiSortKey: string, ascending: boolean) => void;
}

const INQUIRY_SORT_GROUPS = [
  { heading: "Last name", sortApiKey: "student_last_name", dateColumn: false },
  { heading: "Service", sortApiKey: "service", dateColumn: false },
  { heading: "Status", sortApiKey: "stage", dateColumn: false },
  { heading: "Status start date", sortApiKey: "stage_start_date", dateColumn: true }
] as const;

/** Left ↑ / right ↓ — thick strokes, clear gap between columns; size from `.inquiry-list-sort-trigger-svg` in list-header.css */
const InquiryListSortTriggerIcon: FC = () => (
  <svg className="inquiry-list-sort-trigger-svg" viewBox="0 0 38 38" aria-hidden focusable="false">
    <g fill="currentColor">
      {/* Left ↑ — head + longer stem */}
      <path d="M8 1L1 15h14L8 1z" />
      <path d="M6 15h4v23H6z" />
      {/* Right ↓ — shifted right for more space between columns */}
      <path d="M25 2h4v21h-4z" />
      <path d="M27 37L20 23h14L27 37z" />
    </g>
  </svg>
);

/** Toolbar control (e.g. ListHeader): opens menu for all inquiry list sort keys. */
export const InquiryListSortMenu: FC<InquiryListSortMenuProps> = ({ sortBy, onApplySort }) => (
  <Dropdown onClick={e => e.stopPropagation()}>
    <Dropdown.Toggle
      variant="link"
      size="sm"
      className="btn btn-sm list-header-toolbar-action inquiry-list-sort-toolbar-toggle border-0 text-decoration-none py-0 px-1 shadow-none"
      aria-label="Sort list: choose column and direction"
      title="Sort list"
    >
      <InquiryListSortTriggerIcon />
    </Dropdown.Toggle>
    <Dropdown.Menu align="end" className="inquiry-list-sort-menu-dropdown" onClick={e => e.stopPropagation()}>
      <div className="px-2 py-1 small text-muted border-bottom fw-semibold">Sort by</div>
      {INQUIRY_SORT_GROUPS.map((group, index) => {
        const ascending = sortBy === group.sortApiKey;
        const descending = sortBy === `-${group.sortApiKey}`;
        const ascLabel = group.dateColumn ? "Oldest" : "A → Z";
        const descLabel = group.dateColumn ? "Newest" : "Z → A";
        const isLast = index === INQUIRY_SORT_GROUPS.length - 1;
        return (
          <div
            key={group.sortApiKey}
            className={classNames(
              "px-2 py-1 border-bottom inquiry-list-sort-menu-section",
              isLast && "border-bottom-0"
            )}
          >
            <div className="small text-muted mb-1">{group.heading}</div>
            <div className="d-flex gap-1 inquiry-list-sort-menu-btn-row">
              <Button
                type="button"
                variant={ascending ? "primary" : "outline-secondary"}
                size="sm"
                className="flex-fill inquiry-column-menu-sort-btn"
                onClick={e => {
                  e.stopPropagation();
                  onApplySort(group.sortApiKey, true);
                }}
              >
                {ascLabel}
              </Button>
              <Button
                type="button"
                variant={descending ? "primary" : "outline-secondary"}
                size="sm"
                className="flex-fill inquiry-column-menu-sort-btn"
                onClick={e => {
                  e.stopPropagation();
                  onApplySort(group.sortApiKey, false);
                }}
              >
                {descLabel}
              </Button>
            </div>
          </div>
        );
      })}
    </Dropdown.Menu>
  </Dropdown>
);
